import { Fragment, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  DailyGridColumn,
  DailyGridFooter,
  DailyGridHeader,
  DailyGridRow,
  DailyGridStockDetail,
} from "../../../types/sale-daily-grid.type";
import { formatNumber } from "../../../utils";
import {
  buildCellByCode,
  clampZoom,
  FROZEN_COLUMNS,
  FROZEN_STICKY_COUNT,
  FROZEN_STICKY_WIDTH,
  FROZEN_TOTAL_WIDTH,
  getEditKey,
  getFrozenLeftOffsets,
  getRowKey,
  PAYMENT_QR_LABELS,
  paymentChannelDisplay,
  PaymentQrState,
  paymentQrState,
  paymentQrTooltip,
  PRODUCT_SUBCOLUMNS,
  resolveCellValues,
  RowMoney,
  scaledSubColWidth,
  SUMMARY_COLUMNS,
  SUMMARY_TOTAL_WIDTH,
  SUMMARY_VALUE_GROUP_LABEL,
  ZOOM_DEFAULT,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
} from "./dailyGrid.utils";
import EditableCell from "./EditableCell";

interface DailyGridProps {
  /** Report header — supplies the authoritative right-summary footer totals. */
  header: DailyGridHeader;
  columns: DailyGridColumn[];
  rows: DailyGridRow[];
  /** Authoritative footer totals (stock snapshot + total fine). */
  footer?: DailyGridFooter;
  /** View-only: hide all edit affordances (default true for now). */
  readOnly?: boolean;
  /** Click handler for a bill row (navigates to its transaction detail). */
  onBillClick?: (row: DailyGridRow) => void;
}

/** "11:22:52" -> "11:22" (HH:MM). */
const toHHMM = (t: string | null | undefined): string | null => {
  if (!t) return null;
  const [h, m] = t.split(":");
  return h != null && m != null ? `${h}:${m}` : t;
};

/** "01:21:08" -> "1:21" (H:MM, hours without leading zero). */
const toHMM = (t: string | null | undefined): string | null => {
  if (!t) return null;
  const [h, m] = t.split(":");
  return h != null && m != null ? `${parseInt(h, 10)}:${m}` : t;
};

// Layout constants. The LOCKED area (frozen-left columns, header row heights,
// footer label/row heights, and the now frozen-RIGHT summary columns) is a fixed
// size; only the product matrix scales with zoom.
const HEADER_BG = "#eef0f6";
const SUBHEAD_BG = "#f3f4fa";
const FOOTER_BG = "#eceef6";
const SUMMARY_BG = "#f6f7fb";
const SUMMARY_HEAD_BG = "#eceef6";
const BORDER = "#e2e2ec";
const ROW_LINE = "#ededf3";
const FROZEN_SEP = "#c4c7d6";
const NEGATIVE = "#c62828";
const POSITIVE = "#2e7d32";
const GROUP_H = 104;
const SUB_H = 22;
const FOOTER_ROW_H = 24;
const FOOTER_ROWS = 5;
const SUBCOL_COLORS = ["#2e7d32", "#c62828", "#ef6c00"]; // ขาย / เสีย / เหลือ
// Sub-column meaning is now encoded by cell BACKGROUND (the header labels are
// dropped per design): ขาย = white, เสีย = light-red, เหลือ = light-yellow.
const SUBCOL_BG = ["#ffffff", "#FCE7E7", "#FEF6DC"];
const SUBCOL_LEGEND = ["ขาย", "เสีย", "เหลือ (บนเชลฟ์ลูกค้า)"];
const SUMMARY_FONT = "0.72rem";

// Row state tints. A store with a name but NO cells = missed visit (red text on
// yellow). An off-pathway visit gets a light-pink wash instead of a text tag.
// QR swatch backgrounds (ข้อ 2.4 ข): ชมพู / แดง / ขาว / เหลือง.
//
// Pink and yellow are lifted straight from this grid's own canonical Thai day
// palette (THAI_DAY_COLORS in dailyGrid.utils — อังคาร.pale and จันทร์.pale) so
// the report keeps one colour vocabulary. The red is deliberately NOT the
// matching อาทิตย์.pale (#FAD7D5): that pale sits too close to the pink, and
// these two encode paid vs unpaid — the one distinction that must never be
// ambiguous. It is a mid-tone stepped down from อาทิตย์.bg (#E5534B) instead.
//
// Colour is NEVER the only carrier: every swatch also renders a short text token
// and a Tooltip (see PAYMENT_QR_LABELS).
const QR_PAID_BG = "#FBD9E7"; // ชมพู
const QR_PENDING_BG = "#F2A29C"; // แดง
const QR_CASH_OTHER_BG = "#ffffff"; // ขาว
const QR_CREDIT_BG = "#FBF1C9"; // เหลือง

// Unreadable status/channel. Deliberately NOT a fifth flat colour: a solid
// swatch would read as just another valid category in the four-colour scheme.
// Grey diagonal hatching reads as "this cell is broken" at a glance and
// survives greyscale printing, where a fifth pastel would not.
//
// Applied as `backgroundImage`, not via the `background` shorthand: the
// shorthand would also have to restate the base colour, and layering the
// gradient over whatever tint the row already has keeps missed-visit and
// off-pathway rows readable underneath the hatch.
const QR_UNKNOWN_BASE = "#F2F2F4";
const QR_UNKNOWN_HATCH =
  "repeating-linear-gradient(45deg, #D6D6DB 0, #D6D6DB 5px, #F2F2F4 5px, #F2F2F4 10px)";
const QR_UNKNOWN_FG = "#B3261E";

/** Swatch state -> background. `none` is absent so the row tint shows through. */
const QR_SWATCH_BG: Partial<Record<PaymentQrState, string>> = {
  "qr-paid": QR_PAID_BG,
  "qr-pending": QR_PENDING_BG,
  "cash-other": QR_CASH_OTHER_BG,
  credit: QR_CREDIT_BG,
  "qr-unknown": QR_UNKNOWN_BASE,
};

const MISSING_BG = "#FFF7DA";
const MISSING_FG = "#C62828";
const OFF_PATHWAY_BG = "#FCE8F0";

// z-index tiers. Corners (cells sticky in TWO directions) must sit above every
// single-direction sticky cell, hence the highest values.
const Z = {
  productBody: 0,
  frozenLeftBody: 3,
  frozenRightBody: 4,
  productHeader: 5,
  productFooter: 5,
  frozenRightHeader: 6, // top + right corner
  frozenLeftHeader: 7, // top + left corner
  frozenRightFooter: 8, // bottom + right corner
  frozenLeftFooter: 9, // bottom + left corner
};

const dash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === "" ? "-" : v;

/** Money cell text — show "-" for an empty/zero figure to cut spreadsheet noise. */
const money = (v: number): ReactNode => (v ? formatNumber(v) : "-");

export default function DailyGrid({
  header,
  columns,
  rows,
  footer,
  readOnly = true,
  onBillClick,
}: DailyGridProps) {
  const [zoom, setZoom] = useState<number>(ZOOM_DEFAULT);
  const [active, setActive] = useState<{ r: number; c: number; f: number } | null>(
    null,
  );
  const [edits, setEdits] = useState<Map<string, number>>(() => new Map());
  // When true, the next input blur is a side effect of Tab/Escape and must not
  // re-commit or re-close (it would clobber the freshly-activated next cell).
  const skipBlur = useRef<boolean>(false);

  // A bill is clickable only when the payload carries its uuid (the route param).
  const goToBill = useCallback(
    (row: DailyGridRow) => {
      if (row.bill_uuid) onBillClick?.(row);
    },
    [onBillClick],
  );

  const frozenOffsets = useMemo(() => getFrozenLeftOffsets(), []);
  // Only the first FROZEN_STICKY_COUNT leading columns stay pinned; this is the
  // last of them (draws the pinned/scroll separator). The remaining leading
  // columns and the right-summary columns now scroll with the product matrix.
  const lastStickyIndex = FROZEN_STICKY_COUNT - 1;

  // Product matrix geometry/fonts derived from zoom (locked area is unaffected).
  const subW = scaledSubColWidth(zoom);
  const groupW = subW * PRODUCT_SUBCOLUMNS.length;
  const footerFont = `${(0.72 * zoom).toFixed(3)}rem`;

  // One product_code -> cell map per row. Rebuilt only when rows change.
  const rowMaps = useMemo(() => rows.map((row) => buildCellByCode(row.cells)), [
    rows,
  ]);

  // product_code -> authoritative stock snapshot (footer rows). Empty when the
  // payload predates the footer addition (values then fall back to "-").
  const stockByCode = useMemo(() => {
    const map = new Map<string, DailyGridStockDetail>();
    footer?.stock_detail.forEach((s) => map.set(s.product_code, s));
    return map;
  }, [footer]);

  const displayedValue = useCallback(
    (r: number, c: number, f: number): { value: number; edited: boolean } => {
      const col = columns[c];
      const field = PRODUCT_SUBCOLUMNS[f].field;
      const original = resolveCellValues(rowMaps[r], col.product_code)[field];
      const key = getEditKey(getRowKey(rows[r], r), col.product_code, field);
      if (edits.has(key)) {
        return { value: edits.get(key) as number, edited: true };
      }
      return { value: original, edited: false };
    },
    [columns, rowMaps, rows, edits],
  );

  // --- editing handlers (stable; commit via functional setState) ------------
  const commit = useCallback(
    (r: number, c: number, f: number, raw: string) => {
      const trimmed = raw.trim();
      if (trimmed === "") return; // empty -> keep previous value
      const num = Number(trimmed);
      if (!Number.isFinite(num) || num < 0) return;

      const col = columns[c];
      const field = PRODUCT_SUBCOLUMNS[f].field;
      const original = resolveCellValues(rowMaps[r], col.product_code)[field];
      const key = getEditKey(getRowKey(rows[r], r), col.product_code, field);

      setEdits((prev) => {
        const next = new Map(prev);
        if (num === original) next.delete(key); // reverted -> clear highlight
        else next.set(key, num);
        return next;
      });
    },
    [columns, rowMaps, rows],
  );

  const moveActive = useCallback(
    (r: number, c: number, f: number, dir: 1 | -1) => {
      const perRow = columns.length * PRODUCT_SUBCOLUMNS.length;
      if (perRow === 0) {
        setActive(null);
        return;
      }
      let lin = c * PRODUCT_SUBCOLUMNS.length + f + dir;
      let nr = r;
      if (lin < 0) {
        nr = r - 1;
        lin = perRow - 1;
      } else if (lin >= perRow) {
        nr = r + 1;
        lin = 0;
      }
      if (nr < 0 || nr >= rows.length) {
        setActive(null);
        return;
      }
      setActive({
        r: nr,
        c: Math.floor(lin / PRODUCT_SUBCOLUMNS.length),
        f: lin % PRODUCT_SUBCOLUMNS.length,
      });
    },
    [columns.length, rows.length],
  );

  const onActivate = useCallback((r: number, c: number, f: number) => {
    setActive({ r, c, f });
  }, []);
  const onEnter = useCallback(
    (r: number, c: number, f: number, raw: string) => {
      commit(r, c, f, raw);
      skipBlur.current = true;
      setActive(null);
    },
    [commit],
  );
  const onTab = useCallback(
    (r: number, c: number, f: number, raw: string, dir: 1 | -1) => {
      commit(r, c, f, raw);
      skipBlur.current = true;
      moveActive(r, c, f, dir);
    },
    [commit, moveActive],
  );
  const onEscape = useCallback(() => {
    skipBlur.current = true;
    setActive(null);
  }, []);
  const onBlur = useCallback(
    (r: number, c: number, f: number, raw: string) => {
      if (skipBlur.current) {
        skipBlur.current = false;
        return;
      }
      commit(r, c, f, raw);
      setActive(null);
    },
    [commit],
  );

  // --- per-product totals (over displayed values) ---------------------------
  const totals = useMemo(() => {
    return columns.map((col) =>
      PRODUCT_SUBCOLUMNS.map((sub) => {
        let sum = 0;
        for (let r = 0; r < rows.length; r++) {
          const original = resolveCellValues(rowMaps[r], col.product_code)[
            sub.field
          ];
          const key = getEditKey(getRowKey(rows[r], r), col.product_code, sub.field);
          sum += edits.has(key) ? (edits.get(key) as number) : original;
        }
        return sum;
      }),
    );
  }, [columns, rows, rowMaps, edits]);

  // --- per-row money summary (from the transaction, carried on each row) ------
  // The daily-grid API returns the real per-bill money (sale_report_item), with
  // the ex-VAT value already routed into เงินเชื่อ / เงินสด by the backend — the
  // same figures the Excel export prints. Editing a sold quantity does not change
  // them: they reflect the actual bill.
  const rowMoney = useMemo<RowMoney[]>(
    () =>
      rows.map((row) => ({
        creditValue: row.credit_value ?? 0,
        cashValue: row.cash_value ?? 0,
        vat: row.vat_total ?? 0,
        totalSale: row.net_total ?? 0,
      })),
    [rows],
  );

  // Authoritative right-summary footer totals come from the report HEADER (not
  // the FE money assumption): รวมเงินเชื่อ / รวมเงินสด / ภาษี / รวมยอดขาย.
  const headerTotals: RowMoney = {
    creditValue: header.credit_total_value ?? 0,
    cashValue: header.cash_total_value ?? 0,
    vat: header.total_vat ?? 0,
    totalSale: header.total_price ?? 0,
  };

  // The GRAND TOTAL on footer row 2 reports the sale total BEFORE VAT, and is
  // deliberately NOT the same figure as the รวมยอดขาย column total directly
  // above it. That one is a column total sitting under per-row `net_total`
  // (VAT-inclusive) and has to keep matching its own column; this one answers
  // "ยอดขายก่อนภาษี" and reconciles against รวมเงินเชื่อ + รวมเงินสด, both of
  // which are already ex-VAT. The two differ by exactly ภาษี, so that difference
  // has to be spelled out somewhere or the pair reads as an arithmetic bug —
  // here it lives in the cell's tooltip rather than inline, to keep the figure
  // itself readable at a glance.
  //
  // Falls back to total_price − total_vat when the backend has not yet shipped
  // total_before_vat; the identity is exact (amount_total ≡ net_total − vat_total).
  const grandTotalBeforeVat =
    header.total_before_vat ?? (header.total_price ?? 0) - (header.total_vat ?? 0);

  // คงเหลือ(ตามบัญชี), ผลต่าง and ค่าปรับรวม are all backend figures
  // (`book_remain_qty`, `pda_diff_qty`, `footer.fine`). They used to be derived
  // here; they moved so the screen and the Excel export cannot disagree.
  const fineTotal = footer?.fine ?? 0;

  const editedCount = edits.size;
  const zoomPct = Math.round(zoom * 100);
  const hasColumns = columns.length > 0;
  const hasRows = rows.length > 0;
  const bodyColSpan =
    FROZEN_COLUMNS.length +
    (hasColumns
      ? columns.length * PRODUCT_SUBCOLUMNS.length + SUMMARY_COLUMNS.length
      : 1);

  // --- shared sticky style builders -----------------------------------------
  // A leading column is PINNED (sticky-left) only when its index is within the
  // first FROZEN_STICKY_COUNT; the rest still pin to the TOP (header) / BOTTOM
  // (footer) but scroll horizontally with the product matrix.
  const frozenHeaderStyle = (i: number): React.CSSProperties => {
    const pinned = i < FROZEN_STICKY_COUNT;
    return {
      position: "sticky",
      top: 0,
      ...(pinned
        ? { left: frozenOffsets[i], zIndex: Z.frozenLeftHeader }
        : { zIndex: Z.productHeader }),
      background: HEADER_BG,
      width: FROZEN_COLUMNS[i].width,
      minWidth: FROZEN_COLUMNS[i].width,
      maxWidth: FROZEN_COLUMNS[i].width,
      height: GROUP_H + SUB_H,
      padding: "4px 8px",
      boxSizing: "border-box",
      textAlign: FROZEN_COLUMNS[i].align,
      verticalAlign: "middle",
      fontSize: "0.76rem",
      fontWeight: 600,
      borderBottom: `1px solid ${BORDER}`,
      borderRight:
        i === lastStickyIndex ? `2px solid ${FROZEN_SEP}` : `1px solid ${BORDER}`,
    };
  };

  const frozenBodyStyle = (
    i: number,
    rowBg: string,
    align: "left" | "center",
    color?: string,
  ): React.CSSProperties => {
    const pinned = i < FROZEN_STICKY_COUNT;
    return {
      ...(pinned
        ? { position: "sticky", left: frozenOffsets[i], zIndex: Z.frozenLeftBody }
        : { zIndex: Z.productBody }),
      background: rowBg,
      color,
      fontWeight: color ? 600 : undefined,
      width: FROZEN_COLUMNS[i].width,
      minWidth: FROZEN_COLUMNS[i].width,
      maxWidth: FROZEN_COLUMNS[i].width,
      padding: "3px 8px",
      boxSizing: "border-box",
      textAlign: align,
      fontSize: "0.78rem",
      borderBottom: `1px solid ${ROW_LINE}`,
      borderRight:
        i === lastStickyIndex ? `2px solid ${FROZEN_SEP}` : `1px solid ${BORDER}`,
    };
  };

  // --- right-summary cell builders (scroll horizontally; pin only to top) ----
  const summaryHeaderStyle = (
    idx: number,
    top: number,
    rowSpan2: boolean,
  ): React.CSSProperties => ({
    position: "sticky",
    top,
    zIndex: Z.productHeader,
    background: HEADER_BG,
    width: SUMMARY_COLUMNS[idx].width,
    minWidth: SUMMARY_COLUMNS[idx].width,
    maxWidth: SUMMARY_COLUMNS[idx].width,
    height: rowSpan2 ? GROUP_H + SUB_H : SUB_H,
    padding: "4px 6px",
    boxSizing: "border-box",
    textAlign: "center",
    verticalAlign: "middle",
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#3a3a44",
    borderBottom: `1px solid ${BORDER}`,
    borderRight: `1px solid ${BORDER}`,
    borderLeft: idx === 0 ? `2px solid ${FROZEN_SEP}` : undefined,
  });

  const summaryBodyStyle = (idx: number, value: number): React.CSSProperties => ({
    width: SUMMARY_COLUMNS[idx].width,
    minWidth: SUMMARY_COLUMNS[idx].width,
    maxWidth: SUMMARY_COLUMNS[idx].width,
    height: 28,
    padding: "0 8px",
    textAlign: "right",
    fontSize: SUMMARY_FONT,
    fontWeight: SUMMARY_COLUMNS[idx].key === "totalSale" ? 700 : 500,
    color: value ? "#2a2a33" : "#c4c4cf",
    background: SUMMARY_BG,
    boxSizing: "border-box",
    borderBottom: `1px solid ${ROW_LINE}`,
    borderRight:
      idx === SUMMARY_COLUMNS.length - 1 ? undefined : `1px solid ${ROW_LINE}`,
    borderLeft: idx === 0 ? `2px solid ${FROZEN_SEP}` : undefined,
  });

  // Footer cells: frozen-left label (corner) and product cells, both sticky to
  // the bottom. Stacked rows offset their `bottom` like the header offsets top.
  const footerBottom = (rowIdx: number) =>
    (FOOTER_ROWS - 1 - rowIdx) * FOOTER_ROW_H;

  const footerLabelStyle = (rowIdx: number): React.CSSProperties => ({
    position: "sticky",
    left: 0,
    bottom: footerBottom(rowIdx),
    zIndex: Z.frozenLeftFooter,
    background: FOOTER_BG,
    width: FROZEN_STICKY_WIDTH,
    minWidth: FROZEN_STICKY_WIDTH,
    maxWidth: FROZEN_STICKY_WIDTH,
    height: FOOTER_ROW_H,
    padding: "2px 10px",
    boxSizing: "border-box",
    textAlign: "right",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#3a3a44",
    borderTop: rowIdx === 0 ? `2px solid ${FROZEN_SEP}` : `1px solid ${ROW_LINE}`,
    borderRight: `2px solid ${FROZEN_SEP}`,
  });

  // Filler under the now-scrolling leading columns (เวลาเข้า / ระยะเวลา / เลขบิล):
  // pins to the bottom like the rest of the footer but scrolls horizontally.
  const footerFillerStyle = (rowIdx: number): React.CSSProperties => ({
    position: "sticky",
    bottom: footerBottom(rowIdx),
    zIndex: Z.productFooter,
    background: FOOTER_BG,
    width: FROZEN_TOTAL_WIDTH - FROZEN_STICKY_WIDTH,
    boxSizing: "border-box",
    height: FOOTER_ROW_H,
    borderTop: rowIdx === 0 ? `2px solid ${FROZEN_SEP}` : `1px solid ${ROW_LINE}`,
  });

  // Footer label cell (pinned) + filler cell (scrolling) — together they span
  // all leading columns, matching the body/header column count.
  const renderFooterLabel = (rowIdx: number, content: ReactNode) => (
    <>
      <td colSpan={FROZEN_STICKY_COUNT} style={footerLabelStyle(rowIdx)}>
        {content}
      </td>
      <td
        colSpan={FROZEN_COLUMNS.length - FROZEN_STICKY_COUNT}
        style={footerFillerStyle(rowIdx)}
      />
    </>
  );

  const footerProductStyle = (
    rowIdx: number,
    color?: string,
  ): React.CSSProperties => ({
    position: "sticky",
    bottom: footerBottom(rowIdx),
    zIndex: Z.productFooter,
    background: FOOTER_BG,
    height: FOOTER_ROW_H,
    textAlign: "center",
    fontSize: footerFont,
    fontWeight: 700,
    color,
    boxSizing: "border-box",
    borderTop: rowIdx === 0 ? `2px solid ${FROZEN_SEP}` : `1px solid ${ROW_LINE}`,
  });

  /**
   * Render the product-matrix footer cells for one footer row. `cellFor`
   * returns the content for sub-column f of product c; `colorFor` its colour.
   * Every cell is `subW` wide so the footer scrolls + zooms in lock-step with
   * the body.
   */
  const renderProductFooterCells = (
    rowIdx: number,
    cellFor: (c: number, f: number) => ReactNode,
    colorFor?: (c: number, f: number) => string | undefined,
  ) =>
    columns.map((col, c) => (
      <Fragment key={col.product_code}>
        {PRODUCT_SUBCOLUMNS.map((sub, f) => (
          <td
            key={sub.field}
            style={{
              ...footerProductStyle(rowIdx, colorFor?.(c, f)),
              width: subW,
              minWidth: subW,
              maxWidth: subW,
              borderLeft:
                c === 0 && f === 0 ? `2px solid ${FROZEN_SEP}` : undefined,
              borderRight:
                f === PRODUCT_SUBCOLUMNS.length - 1
                  ? `1px solid ${BORDER}`
                  : `1px solid ${ROW_LINE}`,
            }}
          >
            {cellFor(c, f)}
          </td>
        ))}
      </Fragment>
    ));

  // --- right-summary footer (frozen-right + sticky-bottom) ------------------
  // Base style for a frozen-right footer cell on a given footer row.
  const summaryFooterCell = (
    rowIdx: number,
    width: number,
    extra: React.CSSProperties,
  ): React.CSSProperties => ({
    position: "sticky",
    bottom: footerBottom(rowIdx),
    zIndex: Z.productFooter,
    width,
    minWidth: width,
    maxWidth: width,
    height: FOOTER_ROW_H,
    boxSizing: "border-box",
    borderTop: rowIdx === 0 ? `2px solid ${FROZEN_SEP}` : `1px solid ${ROW_LINE}`,
    ...extra,
  });

  // Four per-column summary footer cells (label row / value row).
  const renderSummaryFooterColumns = (
    rowIdx: number,
    contentFor: (idx: number) => ReactNode,
    headRow: boolean,
  ) =>
    SUMMARY_COLUMNS.map((sc, idx) => (
      <td
        key={sc.key}
        style={summaryFooterCell(rowIdx, sc.width, {
          background: headRow ? SUMMARY_HEAD_BG : SUMMARY_BG,
          textAlign: headRow ? "center" : "right",
          padding: headRow ? "2px 4px" : "2px 8px",
          fontSize: headRow ? "0.6rem" : SUMMARY_FONT,
          fontWeight: 700,
          color: "#3a3a44",
          borderLeft: idx === 0 ? `2px solid ${FROZEN_SEP}` : `1px solid ${ROW_LINE}`,
        })}
      >
        {contentFor(idx)}
      </td>
    ));

  // One full-width (colSpan 4) summary footer cell (grand-total / diff / blank).
  const summaryFooterSpan = (
    rowIdx: number,
    content: ReactNode,
    extra: React.CSSProperties,
  ) => (
    <td
      colSpan={SUMMARY_COLUMNS.length}
      style={summaryFooterCell(rowIdx, SUMMARY_TOTAL_WIDTH, {
        background: SUMMARY_BG,
        textAlign: "center",
        padding: "2px 8px",
        borderLeft: `2px solid ${FROZEN_SEP}`,
        ...extra,
      })}
    >
      {content}
    </td>
  );

  const summaryHeadLabels = ["รวมเงินเชื่อ", "รวมเงินสด", "ภาษี", "รวมยอดขาย"];

  return (
    <Box>
      {/* Toolbar: zoom + edit status ---------------------------------------- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
          ขนาดตาราง
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
          }}
        >
          <Tooltip title="ย่อ">
            <span>
              <IconButton
                size="small"
                disabled={zoom <= ZOOM_MIN}
                onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ minWidth: 44, textAlign: "center", fontWeight: 600 }}
          >
            {zoomPct}%
          </Typography>
          <Tooltip title="ขยาย">
            <span>
              <IconButton
                size="small"
                disabled={zoom >= ZOOM_MAX}
                onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Tooltip title="รีเซ็ตขนาด">
          <span>
            <IconButton
              size="small"
              disabled={zoom === ZOOM_DEFAULT}
              onClick={() => setZoom(ZOOM_DEFAULT)}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        {!readOnly && editedCount > 0 && (
          <>
            <Chip
              size="small"
              label={`แก้ไข ${editedCount} ช่อง (ฉบับร่าง • ยังไม่บันทึก)`}
              sx={{
                bgcolor: "#FFF3CD",
                color: "#7a5b00",
                fontWeight: 600,
                height: 26,
              }}
            />
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setEdits(new Map())}
            >
              รีเซ็ตการแก้ไข
            </Button>
          </>
        )}
      </Box>

      {/* Legend — sub-column colour key + row-state colours -------------------- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1,
        }}
      >
        {SUBCOL_LEGEND.map((label, f) => (
          <Box
            key={label}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "3px",
                border: `1px solid ${BORDER}`,
                bgcolor: SUBCOL_BG[f],
              }}
            />
            <Typography variant="caption" sx={{ color: "#555" }}>
              {label}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "3px",
              border: `1px solid ${BORDER}`,
              bgcolor: MISSING_BG,
            }}
          />
          <Typography variant="caption" sx={{ color: MISSING_FG, fontWeight: 600 }}>
            ขาดการเข้าร้าน
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "3px",
              border: `1px solid ${BORDER}`,
              bgcolor: OFF_PATHWAY_BG,
            }}
          />
          <Typography variant="caption" sx={{ color: "#555" }}>
            นอกเส้นทาง
          </Typography>
        </Box>
      </Box>

      {/* Scroll viewport ---------------------------------------------------- */}
      <Box
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          overflow: "auto",
          maxHeight: { xs: "58vh", md: "66vh" },
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
            width: "max-content",
            fontFamily: "inherit",
          }}
        >
          {/* Header: product groups (row 1) + sub-columns (row 2) */}
          <thead>
            <tr>
              {FROZEN_COLUMNS.map((col, i) => (
                <th key={col.key} rowSpan={2} style={frozenHeaderStyle(i)}>
                  {col.label}
                  {col.subLabel && (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        fontWeight: 400,
                        fontSize: "0.64rem",
                        color: "#8a8a8a",
                      }}
                    >
                      {col.subLabel}
                    </Box>
                  )}
                </th>
              ))}

              {!hasColumns && (
                <th
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: Z.productHeader,
                    background: HEADER_BG,
                    padding: "8px 16px",
                    height: GROUP_H + SUB_H,
                    color: "#8a8a8a",
                    fontWeight: 500,
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  ไม่มีคอลัมน์สินค้า
                </th>
              )}

              {columns.map((col, c) => (
                <th
                  key={col.product_code}
                  colSpan={PRODUCT_SUBCOLUMNS.length}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: Z.productHeader,
                    background: HEADER_BG,
                    width: groupW,
                    minWidth: groupW,
                    height: GROUP_H,
                    padding: "4px 4px",
                    boxSizing: "border-box",
                    textAlign: "center",
                    verticalAlign: "top",
                    borderBottom: `1px solid ${BORDER}`,
                    borderRight: `1px solid ${BORDER}`,
                    borderLeft: c === 0 ? `2px solid ${FROZEN_SEP}` : undefined,
                  }}
                >
                  {/* Product name (top) + ราคา / คงเหลือ metadata beneath. Name
                      may wrap; ราคา/คงเหลือ are enlarged for readability. */}
                  <Box
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: "#1f1f29",
                      lineHeight: 1.18,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                    title={col.product_name}
                  >
                    {col.product_name}
                  </Box>
                  <Box
                    sx={{
                      mt: 0.5,
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "#1f1f29",
                      lineHeight: 1.4,
                    }}
                  >
                    <Box component="span" sx={{ display: "block" }}>
                      ราคา {col.sell_price == null ? "-" : formatNumber(col.sell_price)}
                    </Box>
                    <Box component="span" sx={{ display: "block" }}>
                      คงเหลือ(ตามบัญชี) {col.book_remain_qty}
                    </Box>
                  </Box>
                </th>
              ))}

              {/* Right-summary group header: มูลค่าสินค้า (colSpan 2), frozen right */}
              {hasColumns && (
                <>
                  <th
                    colSpan={2}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: Z.productHeader,
                      background: HEADER_BG,
                      width: SUMMARY_COLUMNS[0].width + SUMMARY_COLUMNS[1].width,
                      height: GROUP_H,
                      padding: "4px 6px",
                      boxSizing: "border-box",
                      textAlign: "center",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      verticalAlign: "middle",
                      color: "#3a3a44",
                      borderBottom: `1px solid ${BORDER}`,
                      borderRight: `1px solid ${BORDER}`,
                      borderLeft: `2px solid ${FROZEN_SEP}`,
                    }}
                  >
                    {SUMMARY_VALUE_GROUP_LABEL}
                  </th>
                  {SUMMARY_COLUMNS.map((s, idx) =>
                    s.underValueGroup ? null : (
                      <th key={s.key} rowSpan={2} style={summaryHeaderStyle(idx, 0, true)}>
                        {s.label}
                      </th>
                    ),
                  )}
                </>
              )}
            </tr>
            <tr>
              {columns.map((col, c) => (
                <Fragment key={col.product_code}>
                  {PRODUCT_SUBCOLUMNS.map((sub, f) => (
                    // ขาย / เสีย / เหลือ label, on each sub-column's own colour
                    // (white/red/yellow) so the meaning reads from both.
                    <th
                      key={sub.field}
                      style={{
                        position: "sticky",
                        top: GROUP_H,
                        zIndex: Z.productHeader,
                        background: SUBCOL_BG[f],
                        width: subW,
                        minWidth: subW,
                        maxWidth: subW,
                        height: SUB_H,
                        boxSizing: "border-box",
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        color: SUBCOL_COLORS[f],
                        textAlign: "center",
                        borderBottom: `1px solid ${BORDER}`,
                        borderLeft:
                          c === 0 && f === 0
                            ? `2px solid ${FROZEN_SEP}`
                            : undefined,
                        borderRight:
                          f === PRODUCT_SUBCOLUMNS.length - 1
                            ? `1px solid ${BORDER}`
                            : `1px solid ${ROW_LINE}`,
                      }}
                    >
                      {sub.label}
                    </th>
                  ))}
                </Fragment>
              ))}

              {/* Sub-headers under มูลค่าสินค้า: เงินเชื่อ / เงินสด (frozen right) */}
              {hasColumns &&
                SUMMARY_COLUMNS.map((s, idx) =>
                  s.underValueGroup ? (
                    <th
                      key={s.key}
                      style={{
                        ...summaryHeaderStyle(idx, GROUP_H, false),
                        background: SUBHEAD_BG,
                        fontSize: "0.66rem",
                        fontWeight: 600,
                        color: "#555",
                      }}
                    >
                      {s.label}
                    </th>
                  ) : null,
                )}
            </tr>
          </thead>

          <tbody>
            {!hasRows && (
              <tr>
                <td
                  colSpan={bodyColSpan}
                  style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    color: "#8a8a8a",
                    fontWeight: 500,
                  }}
                >
                  ไม่พบข้อมูลแถว
                </td>
              </tr>
            )}

            {hasRows &&
              rows.map((row, r) => {
                // A store with a name but no cells = a missed visit (ขาดการเข้าร้าน).
                const missingVisit =
                  row.cells.length === 0 && !!row.customer_name;
                const rowBg = missingVisit
                  ? MISSING_BG
                  : row.is_off_pathway
                    ? OFF_PATHWAY_BG
                    : r % 2 === 1
                      ? "#f8f8fb"
                      : "#ffffff";
                const rowFg = missingVisit ? MISSING_FG : undefined;
                const m = rowMoney[r];

                return (
                  <tr key={getRowKey(row, r)}>
                    {/* Frozen-left columns (locked, fixed size) */}
                    <td style={frozenBodyStyle(0, rowBg, "center", rowFg)}>
                      {row.no}
                    </td>
                    <td style={frozenBodyStyle(1, rowBg, "left", rowFg)}>
                      {dash(row.customer_code)}
                    </td>
                    <td style={frozenBodyStyle(2, rowBg, "left", rowFg)}>
                      <Tooltip title={row.customer_name ?? ""} placement="top-start">
                        <Box
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {dash(row.customer_name)}
                        </Box>
                      </Tooltip>
                    </td>
                    <td style={frozenBodyStyle(3, rowBg, "center", rowFg)}>
                      {dash(toHHMM(row.start_time))}
                    </td>
                    <td style={frozenBodyStyle(4, rowBg, "center", rowFg)}>
                      {dash(toHMM(row.duration_time))}
                    </td>
                    <td style={frozenBodyStyle(5, rowBg, "left", rowFg)}>
                      {row.bill_id ? (
                        <Box
                          component="span"
                          onClick={
                            row.bill_uuid ? () => goToBill(row) : undefined
                          }
                          sx={{
                            color: "#5C69E8",
                            fontWeight: 500,
                            cursor: row.bill_uuid ? "pointer" : "default",
                            "&:hover": row.bill_uuid
                              ? { textDecoration: "underline" }
                              : undefined,
                          }}
                        >
                          {row.bill_id}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </td>

                    {/* ── 2.4 (ข) column 1: QR-scoped status swatch ──
                        Background carries the state; the text token + Tooltip
                        carry the same meaning for anyone who cannot rely on
                        colour. A row with no bill keeps the row tint. */}
                    <td
                      // Semantic marker for the "cannot read this" state. jsdom's
                      // CSS parser discards gradients, so the hatch alone is not
                      // assertable in tests; this makes the state explicit for
                      // both tests and anyone inspecting the DOM.
                      data-payment-unknown={
                        paymentQrState(row) === "qr-unknown" ? "true" : undefined
                      }
                      style={{
                        ...frozenBodyStyle(
                          6,
                          QR_SWATCH_BG[paymentQrState(row)] ?? rowBg,
                          "center",
                          rowFg,
                        ),
                        ...(paymentQrState(row) === "qr-unknown" && {
                          backgroundImage: QR_UNKNOWN_HATCH,
                        }),
                      }}
                    >
                      {(() => {
                        const state = paymentQrState(row);
                        const { text } = PAYMENT_QR_LABELS[state];
                        if (!text) return "";
                        const isUnknown = state === "qr-unknown";
                        return (
                          <Tooltip title={paymentQrTooltip(row)} placement="top">
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.72rem",
                                // Hatching alone could be mistaken for a print
                                // artefact; the red weight makes it a deliberate
                                // "look at this".
                                ...(isUnknown && {
                                  color: QR_UNKNOWN_FG,
                                  fontWeight: 700,
                                }),
                              }}
                            >
                              {text}
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </td>

                    {/* ── 2.4 (ข) column 2: actual payment channel, all bills ── */}
                    {(() => {
                      const channel = paymentChannelDisplay(row);
                      // Same rule as column 1: a channel we do not recognise gets
                      // the hatched treatment and shows its raw value, so a newly
                      // shipped payment type cannot hide behind "ไม่ระบุ".
                      return (
                        <td
                          data-payment-unknown={
                            channel.isUnrecognised ? "true" : undefined
                          }
                          style={{
                            ...frozenBodyStyle(
                              7,
                              channel.isUnrecognised ? QR_UNKNOWN_BASE : rowBg,
                              "center",
                              channel.isUnrecognised ? QR_UNKNOWN_FG : rowFg,
                            ),
                            ...(channel.isUnrecognised && {
                              backgroundImage: QR_UNKNOWN_HATCH,
                            }),
                          }}
                        >
                          {channel.tooltip ? (
                            <Tooltip title={channel.tooltip} placement="top">
                              <Box component="span">{channel.text}</Box>
                            </Tooltip>
                          ) : (
                            channel.text
                          )}
                        </td>
                      );
                    })()}

                    {/* Product cells (scroll + zoom) */}
                    {!hasColumns && (
                      <td
                        style={{
                          padding: "4px 8px",
                          textAlign: "center",
                          color: "#c4c4cf",
                          background: rowBg,
                          borderBottom: `1px solid ${ROW_LINE}`,
                        }}
                      >
                        -
                      </td>
                    )}

                    {columns.map((col, c) => {
                      return (
                        <Fragment key={col.product_code}>
                          {PRODUCT_SUBCOLUMNS.map((sub, f) => {
                            const { value, edited } = displayedValue(r, c, f);
                            const isLastSub =
                              f === PRODUCT_SUBCOLUMNS.length - 1;
                            return (
                              <EditableCell
                                key={sub.field}
                                r={r}
                                c={c}
                                f={f}
                                value={value}
                                isEdited={edited}
                                isActive={
                                  !!active &&
                                  active.r === r &&
                                  active.c === c &&
                                  active.f === f
                                }
                                width={subW}
                                fontScale={zoom}
                                color={rowFg ?? "#1f1f29"}
                                background={missingVisit ? MISSING_BG : SUBCOL_BG[f]}
                                readOnly={readOnly}
                                borderRight={isLastSub}
                                borderLeft={c === 0 && f === 0}
                                onActivate={onActivate}
                                onEnter={onEnter}
                                onBlur={onBlur}
                                onTab={onTab}
                                onEscape={onEscape}
                              />
                            );
                          })}
                        </Fragment>
                      );
                    })}

                    {/* Right-summary money cells (derived; LOCKED to the right) */}
                    {hasColumns &&
                      SUMMARY_COLUMNS.map((s, idx) => (
                        <td key={s.key} style={summaryBodyStyle(idx, m[s.key])}>
                          {money(m[s.key])}
                        </td>
                      ))}
                  </tr>
                );
              })}
          </tbody>

          {/* Locked footer — left labels + product sums + frozen-right summary */}
          {hasRows && hasColumns && (
            <tfoot>
              {/* Row 0 — left: รวมของขาย; right: summary LABELS */}
              <tr>
                {renderFooterLabel(0, "รวมของขาย [ของเสีย] บนเชลฟ์")}
                {renderProductFooterCells(
                  0,
                  (c, f) => totals[c][f],
                  (_c, f) => SUBCOL_COLORS[f],
                )}
                {renderSummaryFooterColumns(0, (idx) => summaryHeadLabels[idx], true)}
              </tr>

              {/* Row 1 — left: ของเหลือ (receive_remain_qty); right: summary
                  VALUES (from header) */}
              <tr>
                {renderFooterLabel(1, "ของเหลือ")}
                {renderProductFooterCells(1, (c, f) => {
                  if (f !== 2) return "";
                  const st = stockByCode.get(columns[c].product_code);
                  return st?.receive_remain_qty ?? "-";
                })}
                {renderSummaryFooterColumns(
                  1,
                  (idx) => money(headerTotals[SUMMARY_COLUMNS[idx].key]),
                  false,
                )}
              </tr>

              {/* Row 2 — left: เหลือ/เสียจริง (actual waste/remain); right: GRAND
                  TOTAL, reported BEFORE VAT (see grandTotalBeforeVat) */}
              <tr>
                {renderFooterLabel(2, "เหลือ/เสียจริง")}
                {renderProductFooterCells(
                  2,
                  (c, f) => {
                    const st = stockByCode.get(columns[c].product_code);
                    if (!st) return f === 2 ? "-" : "";
                    if (f === 1) return st.actual_waste_qty;
                    if (f === 2) return st.actual_remain_qty;
                    return "";
                  },
                  (_c, f) =>
                    f === 1 ? SUBCOL_COLORS[1] : f === 2 ? "#1f1f29" : undefined,
                )}
                {summaryFooterSpan(
                  2,
                  // Number only. The label rides in a tooltip: spelled out
                  // inline it was the longest string in the footer and read as
                  // a caption competing with the figure, which is the one thing
                  // in this cell anyone is looking for. Tooltip (not `title`)
                  // to match every other hover hint in this grid, and MUI puts
                  // the string on the child as `aria-label`, so the meaning is
                  // still reachable without a mouse.
                  <Tooltip title="รวมยอดขาย (ก่อน VAT)" placement="top">
                    <span>{formatNumber(grandTotalBeforeVat)}</span>
                  </Tooltip>,
                  {
                    fontSize: "0.85rem",
                    fontWeight: 800,
                    color: "#1f1f29",
                  },
                )}
              </tr>

              {/* Row 3 — left: ผลต่าง (backend pda_diff_qty = sell_pda_qty −
                  actual_sell_qty); right: blank (money total lives on the
                  เป็นเงิน [ค่าปรับ] row) */}
              <tr>
                {renderFooterLabel(3, "ผลต่าง")}
                {renderProductFooterCells(
                  3,
                  (c, f) => {
                    if (f !== 2) return "";
                    const diff = columns[c].pda_diff_qty;
                    if (diff == null) return "-";
                    return diff > 0 ? `+${diff}` : diff;
                  },
                  (c, f) => {
                    if (f !== 2) return undefined;
                    const diff = columns[c].pda_diff_qty ?? 0;
                    return diff < 0 ? NEGATIVE : diff > 0 ? POSITIVE : "#999";
                  },
                )}
                {summaryFooterSpan(3, "", {})}
              </tr>

              {/* Row 4 — left: เป็นเงิน (per-product fine); right: ค่าปรับรวม */}
              <tr>
                {renderFooterLabel(4, "เป็นเงิน [ค่าปรับ]")}
                {renderProductFooterCells(
                  4,
                  (c, f) => {
                    if (f !== 2) return "";
                    const st = stockByCode.get(columns[c].product_code);
                    const value = st?.fine ?? 0;
                    return value ? formatNumber(value) : "-";
                  },
                  (c, f) => {
                    if (f !== 2) return undefined;
                    const value = stockByCode.get(columns[c].product_code)?.fine ?? 0;
                    return value < 0 ? NEGATIVE : value > 0 ? POSITIVE : "#999";
                  },
                )}
                {summaryFooterSpan(4, fineTotal ? formatNumber(fineTotal) : "-", {
                  fontSize: SUMMARY_FONT,
                  fontWeight: 800,
                  color: fineTotal < 0 ? NEGATIVE : fineTotal > 0 ? POSITIVE : "#999",
                })}
              </tr>
            </tfoot>
          )}
        </Box>
      </Box>

      {!readOnly && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "#8a8a8a" }}
        >
          คลิกที่ช่อง ขาย/เสีย/เหลือ เพื่อแก้ไข • Enter เพื่อยืนยัน • Esc เพื่อยกเลิก • Tab
          เพื่อไปช่องถัดไป
        </Typography>
      )}
    </Box>
  );
}
